/**
 * Section soft-lock client (Collab Phase 9).
 *
 * Push-mirrored lock table from the server's `lock-state` frames, plus the real
 * collab-guard implementation. Optimistic acquire (editing starts immediately;
 * a lost ~RTT race surfaces as op-rejected lock_held → rollback + toast).
 * Locks renew via the holder's own ops server-side; an explicit re-acquire fires
 * at most every 30 s while still editing. Released on blur/disconnect.
 *
 * Injectable (send/getSelfConnId/toast/now) for tests; no DOM imports.
 */

import { setCollabGuardImpl } from '../editing/collab-guard.js';

const REACQUIRE_INTERVAL_MS = 30_000;

let locks = new Map(); // sectionKey -> {holderSub, holderConn, holderEmail, expiresAt}
let held = new Set(); // keys this client currently holds
let lastAcquireAt = new Map(); // sectionKey -> ms (re-acquire throttle)
let sendLock = null; // (action, sectionKey) => void
let getSelfConnId = () => null;
let toast = null;
let now = () => Date.now();
let installed = false;

function isLockedByPeer(sectionKey) {
  const lock = locks.get(sectionKey);
  if (!lock) return false;
  if (lock.expiresAt && lock.expiresAt <= now()) return false;
  return lock.holderConn !== getSelfConnId();
}

function holderLabel(sectionKey) {
  const lock = locks.get(sectionKey);
  return lock?.holderEmail || 'Someone';
}

function notifyLockedDenied(sectionKey) {
  toast?.(`${holderLabel(sectionKey)} is editing this section`, {
    type: 'info',
    id: `collab-locked-${sectionKey}`,
    duration: 4000
  });
}

function acquire(sectionKey) {
  held.add(sectionKey);
  lastAcquireAt.set(sectionKey, now());
  sendLock?.('acquire', sectionKey);
  return true; // optimistic
}

function release(sectionKey) {
  if (!held.has(sectionKey)) return;
  held.delete(sectionKey);
  lastAcquireAt.delete(sectionKey);
  sendLock?.('release', sectionKey);
}

/** Push-replace the mirrored lock table from a `lock-state` frame. */
export function updateLockState(rows) {
  locks = new Map();
  for (const row of rows || []) if (row?.sectionKey) locks.set(row.sectionKey, row);
}

/** Snapshot of the peer-held locks for presence/iframe treatment (Phase 10). */
export function peerHeldLocks() {
  const self = getSelfConnId();
  const out = [];
  for (const lock of locks.values()) {
    if (lock.expiresAt && lock.expiresAt <= now()) continue;
    if (lock.holderConn === self) continue;
    out.push(lock);
  }
  return out;
}

/**
 * Map peer-held locks to iframe treatment rows {instanceId, name}. Section locks
 * carry the instanceId in the key; shared-section locks resolve it from state.
 */
export function peerLockTreatmentRows(state) {
  const rows = [];
  for (const lock of peerHeldLocks()) {
    const key = lock.sectionKey;
    let instanceId = null;
    if (typeof key !== 'string') continue;
    if (key.startsWith('s:')) instanceId = key.slice(2);
    else if (key === 'shared:navigation')
      instanceId = state?.sharedSections?.navigation?.instanceId || null;
    else if (key === 'shared:footer')
      instanceId = state?.sharedSections?.footer?.instanceId || null;
    if (instanceId) rows.push({ instanceId, name: lock.holderEmail || 'Editing' });
  }
  return rows;
}

/** Re-acquire renewal while still editing (called on presence/editing ticks). */
export function renewIfEditing(sectionKey) {
  if (!held.has(sectionKey)) return;
  const last = lastAcquireAt.get(sectionKey) || 0;
  if (now() - last >= REACQUIRE_INTERVAL_MS) {
    lastAcquireAt.set(sectionKey, now());
    sendLock?.('acquire', sectionKey);
  }
}

export function installCollabLocks({
  send,
  getSelfConnId: selfFn,
  toast: toastFn,
  now: nowFn
} = {}) {
  sendLock = send || null;
  getSelfConnId = selfFn || (() => null);
  toast = toastFn || null;
  now = nowFn || (() => Date.now());
  installed = true;
  setCollabGuardImpl({ isLockedByPeer, notifyLockedDenied, acquire, release });
}

export function uninstallCollabLocks() {
  installed = false;
  setCollabGuardImpl(null);
  locks = new Map();
  held = new Set();
  lastAcquireAt = new Map();
}

export function isCollabLocksInstalled() {
  return installed;
}

// Test seam.
export function _lockState() {
  return { locks, held };
}
